import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useCart } from "../lib/cart";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { inr } from "../lib/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, setQuantity, removeItem, count } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="cart-drawer" side="right" className="bg-white w-[92vw] sm:w-[440px] p-0 flex flex-col">
        <SheetHeader className="p-6 border-b border-slate-100 welljio-mesh">
          <SheetTitle className="font-heading text-2xl text-welljio-ink flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {cart.items.length === 0 && (
            <div className="text-center py-16">
              <p className="text-welljio-muted mb-6">Your cart is empty.</p>
              <Link
                to="/shop"
                data-testid="cart-explore-shop-link"
                onClick={() => setCartOpen(false)}
                className="inline-block bg-welljio-ink text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-slate-800"
              >
                Explore wellness essentials
              </Link>
            </div>
          )}

          {cart.items.map(({ product, quantity, line_total }) => (
            <div key={product.id} className="flex gap-4" data-testid={`cart-item-${product.slug}`}>
              <img src={product.images[0]} alt={product.name} className="w-20 h-20 rounded-2xl object-cover bg-welljio-tint" />
              <div className="flex-1 min-w-0">
                <Link
                  to={`/product/${product.slug}`}
                  onClick={() => setCartOpen(false)}
                  className="font-heading font-medium text-welljio-ink hover:underline line-clamp-1"
                >
                  {product.name}
                </Link>
                <p className="text-xs text-welljio-muted">{product.category}</p>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-welljio-tint rounded-full px-1">
                    <button
                      data-testid={`cart-decrement-${product.slug}`}
                      onClick={() => setQuantity(product.id, Math.max(0, quantity - 1))}
                      className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center"
                    >
                      <Minus className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                    <span className="text-sm w-5 text-center">{quantity}</span>
                    <button
                      data-testid={`cart-increment-${product.slug}`}
                      onClick={() => setQuantity(product.id, quantity + 1)}
                      className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center"
                    >
                      <Plus className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  </div>
                  <p className="text-sm font-medium text-welljio-ink">{inr(line_total)}</p>
                </div>
              </div>
              <button
                data-testid={`cart-remove-${product.slug}`}
                onClick={() => removeItem(product.id)}
                className="text-welljio-subtle hover:text-welljio-ink"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>

        {cart.items.length > 0 && (
          <div className="border-t border-slate-100 p-6 space-y-3">
            <div className="flex justify-between text-sm text-welljio-muted">
              <span>Subtotal</span>
              <span>{inr(cart.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-welljio-muted">
              <span>Shipping</span>
              <span>{cart.shipping === 0 ? "Free" : inr(cart.shipping)}</span>
            </div>
            <div className="flex justify-between font-heading text-lg text-welljio-ink">
              <span>Total</span>
              <span>{inr(cart.total)}</span>
            </div>
            <button
              data-testid="cart-checkout-button"
              onClick={() => {
                setCartOpen(false);
                navigate("/checkout");
              }}
              className="w-full bg-welljio-ink text-white rounded-full py-4 font-medium hover:bg-slate-800 transition-colors"
            >
              Proceed to Checkout
            </button>
            <p className="text-center text-xs text-welljio-subtle">Free shipping on orders above ₹1,499</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
